import { useFlag } from '@reflag/react-sdk';
import React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import { usePaletteHandle } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    type ButtonProps,
    DialogRoot,
    DialogTrigger,
} from '@gitbook/spine-react';
import { type LoadingStateHookResult, useLoadingState } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { useSpaceChangeRequestCommandsFactory } from '../../../Components';
import { useNavigation } from '../../../navigation';
import { NewAgentChangeRequestDialog } from '../Agent/NewAgentChangeRequestDialog';

/**
 * Button to edit the space in a change request.
 */
export function EditSpaceButton(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
}) {
    const { space, analyticsContext } = props;
    const { isEnabled: withDocsAgents } = useFlag('DOCS_AGENTS');

    const loadable = useLoadingState();

    return withDocsAgents ? (
        <ButtonGroup kind="segmented" separatorKind="primary" disabled={loadable[0]} border={false}>
            <EditInCRButton space={space} analyticsContext={analyticsContext} loadable={loadable} />
            <ChangeRequestNewWithAgentButton space={space} />
        </ButtonGroup>
    ) : (
        <EditInCRButton space={space} analyticsContext={analyticsContext} />
    );
}

/**
 * Button to create a change request.
 */
function EditInCRButton(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    loadable?: LoadingStateHookResult;
}) {
    const { space, analyticsContext, loadable } = props;
    const anchorRef = React.useRef(null);
    const handle = usePaletteHandle(anchorRef);
    const { navigate } = useNavigation();

    const getChangeRequestCommands = useSpaceChangeRequestCommandsFactory({
        space,
        analyticsContext,
    });

    return (
        <Button
            label="Edit"
            testID="space.editButton"
            leadingIcon={Icon.ChangeRequestNew}
            kind="primary"
            title="Edit content by starting a new change request"
            ref={anchorRef}
            onPress={async () => {
                const commands = getChangeRequestCommands();
                assert(commands['space.edit'], 'space.edit command must be defined');
                await handle.executeCommand(commands['space.edit']);
                navigate({
                    screen: {
                        ...screen,
                        mode: undefined,
                    },
                });
            }}
            loadable={loadable}
        />
    );
}

/**
 * Button to create a change request with the AI agent.
 */
function ChangeRequestNewWithAgentButton(
    props: Partial<ButtonProps> & {
        space: api.Space;
    }
) {
    const { space } = props;

    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    label="Brainstorm, plan, and implement a change request with GitBook AI"
                    iconOnly
                    leadingIcon={Icon.AIAgent}
                    kind="primary"
                    {...props}
                />
            </DialogTrigger>
            <NewAgentChangeRequestDialog space={space} />
        </DialogRoot>
    );
}
